/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { LONG_DATE_ONLY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Grid, Link, Typography2 } from '@coursera/cds-core';
import { ClockOutlineIcon } from '@coursera/cds-icons';

import type { Scalars } from 'bundles/seo/__generated__/baseContentfulTypes';

type InputProps = {
  title: string;
  publishedDate?: Scalars['DateTime'] | null;
  estimatedReadTimeLabel?: string | null;
  articleUrl: string;
};

export type Props = InputProps;

const styles = {
  relatedArticlesCard: () => css`
    min-width: 304px;
    margin-bottom: 0;
  `,
  articleTitle: () => css`
    margin-bottom: var(--cds-spacing-50);
  `,
  subTitle: () => css`
    align-items: center;
    display: flex;

    svg {
      min-width: 16px;
      padding-right: var(--cds-spacing-50);
    }
  `,
};

/**
 * Renders a card for related articles which includes the title, publication date, estimated read time, and link to the article.
 * @param {Object} props - The properties for the related articles card component.
 * @param {string} props.title - The title of the related article.
 * @param {string} props.publishedDate - The publication date of the related article.
 * @param {string} props.estimatedReadTimeLabel - The estimated read time label for the related article.
 * @param {string} props.articleUrl - The URL of the related article.
 * @returns {React.Element} The rendered related articles card component.
 */
const RelatedArticlesCard = ({ title, publishedDate, estimatedReadTimeLabel, articleUrl }: Props) => {
  const formattedDate = publishedDate && formatDateTimeDisplay(publishedDate, LONG_DATE_ONLY_DISPLAY);
  const subtitle = [estimatedReadTimeLabel, formattedDate].filter(Boolean).join(' · ');

  return (
    <Grid css={styles.relatedArticlesCard} item xs={12} sm={6} md={4} component="li">
      <div css={styles.articleTitle}>
        <Link variant="quiet" typographyVariant="bodyPrimary" href={articleUrl}>
          {title}
        </Link>
      </div>
      {subtitle && (
        <div css={styles.subTitle}>
          {estimatedReadTimeLabel && <ClockOutlineIcon size="small" />}
          <Typography2 variant="bodySecondary" color="supportText" component="span">
            {subtitle}
          </Typography2>
        </div>
      )}
    </Grid>
  );
};

export default RelatedArticlesCard;
